import React,{ useRef, useState,useContext , useEffect, useCallback} from "react";
import { FaTrashAlt, FaPlus, FaChevronDown } from "react-icons/fa";
import "./RightSidebar.css";
import { useRightSidebar } from "../hooks/useRightSidebar";
import { ValidationModal, UploadModal, PreviewModal } from "./ui/RightSidebarModal";
import { Modal } from "../components/ui/Modal";
import { sharedData } from "../sharedData/sharedData";
import { userSession } from '../observers/userObserver';
import config from "../config/config";

const RightSidebar = React.memo((props) => {
  const {
    compliantLetter,
    corporateStructureData,
    managementUboState,
    transactionStatus,
    eddFormData,
    landRegisterCopy,
    RegulatoryListed,
    BillingRecognized,
    setRefreshDocs,
    isPEP,
    InfluenceTransaction
  } = props;
// const [requestStatus, setRequestStatus] = useState("");
// const [status, setStatus] = useState("");
  const {
    currentPage,
    modalOpen,
    setModalOpen,
    modalMessage,
    setModalMessage,
    documents,
    setDocuments,
    certifiedCheckbox,
    setCertifiedCheckbox,
    optionalAttachments,
    setOptionalAttachments,
    optionalCount,
    setOptionalCount,
    uploadingToOptional,
    setUploadingToOptional,
    uploadModalOpen,
    setUploadModalOpen,
    previewModalOpen,
    setPreviewModalOpen,
    previewFile,
    setPreviewFile,
    currentDocType,
    setCurrentDocType,
    file,
    setFile,
    dragging,
    setDragging,
    modalRef,
    addOptionalAttachment,
    handleDeleteOptionalAttachment,
    openUploadModal,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    fileDescriptions,
    handleDescriptionChange,
    uboTypeDocuments,
    trusteeTypeDocuments,
    updateDocumentsForType,
    fetchDocuments,
    openDocumentModal,
    handleFileUpload,
    deleteDocument,
    setFileDescriptions,
    isModalOpen,
    setIsModalOpen,
    modalHeading,
    fileName,
    setFileName,
    status,
    setStatus
  } = useRightSidebar({
    compliantLetter,
    corporateStructureData,
    managementUboState,
    transactionStatus,
    eddFormData,
    landRegisterCopy,
    RegulatoryListed,
    BillingRecognized,
    setRefreshDocs,
    isPEP,
    InfluenceTransaction
  });
 
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // console.log("selectedFile:",selectedFile)
      setFile(selectedFile);
      setFileName(selectedFile.name); // ✅ set file name
    }
  };

  // Confirmation modal for delete
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const pendingDeleteRef = useRef(null);

  const requestDelete = (filename, docType) => {
    pendingDeleteRef.current = { filename, docType };
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (pendingDeleteRef.current) {
      const { filename, docType } = pendingDeleteRef.current;
      deleteDocument(filename, docType);
    }
    pendingDeleteRef.current = null;
    setConfirmDeleteOpen(false);
  };

  const cancelDelete = () => {
    pendingDeleteRef.current = null;
    setConfirmDeleteOpen(false);
  };

  const requiredScrollRef = useRef(null);
  const optionalScrollRef = useRef(null);
  const sidebarRef = useRef(null);
  const placeholderRef = useRef(null);
  const [showRequiredArrow, setShowRequiredArrow] = useState(false);
  const [showOptionalArrow, setShowOptionalArrow] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyDims, setStickyDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const STICKY_TOP = 160;

    const handleScroll = () => {
      if (!sidebarRef.current) return;

      if (isSticky) {
        // Already sticky — use the placeholder (which occupies the sidebar's
        // natural slot) to decide when to release.
        if (placeholderRef.current) {
          const phRect = placeholderRef.current.getBoundingClientRect();
          if (phRect.top > STICKY_TOP) {
            setIsSticky(false);
          }
        }
      } else {
        // Not sticky — use the sidebar itself to decide when to engage.
        const rect = sidebarRef.current.getBoundingClientRect();
        if (rect.top <= STICKY_TOP) {
          setStickyDims({
            width: sidebarRef.current.offsetWidth,
            height: sidebarRef.current.offsetHeight,
          });
          setIsSticky(true);
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isSticky]);

  const checkScroll = useCallback((ref, setter) => {
    if (ref.current) {
      const { scrollTop, scrollHeight, clientHeight } = ref.current;
      setter(scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 10);
    }
  }, []);

  useEffect(() => {
    checkScroll(requiredScrollRef, setShowRequiredArrow);
  }, [documents, checkScroll]);

  useEffect(() => {
    checkScroll(optionalScrollRef, setShowOptionalArrow);
  }, [optionalAttachments, checkScroll]);

  const handleScrollDown = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ top: 100, behavior: 'smooth' });
    }
  };

  const isDisabled = [ "complete", "finalized", "deleted"].includes(status);


  return (<>

    <div
      ref={placeholderRef}
      className={`right-sidebar-placeholder ${isSticky ? 'show' : ''}`}
      style={{ width: isSticky ? stickyDims.width : 0, height: isSticky ? stickyDims.height : 0 }}
    />
    <div
      ref={sidebarRef}
      className={`right-sidebar ${isSticky ? 'is-sticky' : ''}`}
      style={isSticky ? { width: stickyDims.width } : undefined}
    >
      <h3>Required Attachments</h3>

      <div
        className="right-sidebar-content"
        ref={requiredScrollRef}
        onScroll={() => checkScroll(requiredScrollRef, setShowRequiredArrow)}
      >
        <ul>
          {Object.keys(documents).map((docType) => {
            // Multi-file docType (array of files)
            if (Array.isArray(documents[docType])) {
              const files = documents[docType];
              return (
                <li key={docType} style={{ flexDirection: "column", alignItems: "stretch" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>{docType}</span>
                    <button
                      className="icon-btn plus"
                      onClick={() => openUploadModal(docType, false)}
                      disabled={isDisabled}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  {files.map((fileObj) => (
                    <div
                      key={fileObj.filename}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: "12px",
                        marginTop: "4px",
                        fontSize: "13px",
                      }}
                    >
                      <span
                        className="uploaded-file-link"
                        onClick={() => openDocumentModal(docType, fileObj)}
                        style={{ wordBreak: "break-word", flex: 1 }}
                      >
                        {fileObj.certified && (
                          <span role="img" aria-label="Certified">✅</span>
                        )}
                        {fileObj.filename}
                      </span>
                      <button
                        className="icon-btn trash"
                        onClick={() => {
                          if (!fileObj.filename) return;
                          requestDelete(fileObj.filename, docType);
                        }}
                        disabled={isDisabled}
                      >
                        <FaTrashAlt className="trash-icon" />
                      </button>
                    </div>
                  ))}
                </li>
              );
            }

            // Single-file docType (existing logic)
            const isCertified = documents[docType]?.certified || certifiedCheckbox;

            return (
              <li key={docType}>
                {documents[docType] ? (
                  <>
                    <div>
                      <span
                        className="uploaded-file-link"
                        onClick={() => openDocumentModal(docType)}
                      >
                        {isCertified && (
                          <span role="img" aria-label="Certified">
                            ✅
                          </span>
                        )}
                        {docType}
                      </span>

                      {documents[docType]?.description && (
                        <p className="file-description">{documents[docType].description}</p>
                      )}
                    </div>

                    <button
                      className="icon-btn trash"
                      onClick={() => {
                        const filename = documents[docType].filename;
                        if (!filename) {
                          console.error("❌ Error: Missing filename for", docType);
                          return;
                        }
                        requestDelete(filename, docType);

                      }}
                      disabled={isDisabled}
                    >
                      <FaTrashAlt className="trash-icon" />
                    </button>
                  </>
                ) : (
                  <>
                    {docType}
                    <button
                      className="icon-btn plus"
                      onClick={() => openUploadModal(docType, false)}
                       disabled={isDisabled}
                    >
                       <FaPlus />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {showRequiredArrow && (
        <div className="scroll-indicator" onClick={() => handleScrollDown(requiredScrollRef)}>
          <FaChevronDown size={16} />
        </div>
      )}

      {currentPage === "/FinalReview" && (
        <>
          <div style={{ display: "flex", marginBottom: "10px", marginLeft: "0" }}>
            <h3>Optional Attachments</h3>
            <button
              onClick={addOptionalAttachment}
              style={{
                fontSize: "16px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#07b1b0",
              }}
             disabled={isDisabled}
            >
               <FaPlus />
            </button>
          </div>
          <div
            className="right-sidebar-content-optional"
            ref={optionalScrollRef}
            onScroll={() => checkScroll(optionalScrollRef, setShowOptionalArrow)}
          >
            <ul>
              {Object.keys(optionalAttachments).map((docName, idx) => {
                console.log("docName:",docName)
                const isCertified =
                  optionalAttachments[docName]?.certified || certifiedCheckbox;

                return (
                  <li
                    key={docName}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "10px",
                      padding: "4px 0",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {optionalAttachments[docName] ? (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span
                            className="uploaded-file-link"
                            onClick={() => openDocumentModal(docName)}
                            style={{ wordBreak: "break-word", lineHeight: "1.2" }}
                          >
                            {isCertified && (
                              <span role="img" aria-label="Certified">
                                ✅
                              </span>
                            )}
                            {docName}
                          </span>
                          {optionalAttachments[docName]?.description && (
                            <p
                              className="file-description"
                              style={{ margin: 0, fontSize: "12px", color: "#666" }}
                            >
                              {optionalAttachments[docName].description}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span style={{ wordBreak: "break-word", lineHeight: "1.2" }}>
                          {docName}
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "6px",
                        width: "60px",
                        flexShrink: 0,
                      }}
                    >
                      {!optionalAttachments[docName] ? (
                        <>
                          <button
                            onClick={() => openUploadModal(docName, true)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#07b1b0",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                            disabled={isDisabled}
                          >
                             <FaPlus />
                          </button>
                          <button
                            onClick={() => handleDeleteOptionalAttachment(docName)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ff5e5e",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                           disabled={isDisabled}
                          >
                            ➖
                          </button>

                        </>
                      ) : (
                        <button
                          className="icon-btn trash"
                          onClick={() => {
                            const filename = optionalAttachments[docName].filename;
                            if (!filename) {
                              console.error("❌ Error: Missing filename for", docName);
                              return;
                            }
                            requestDelete(filename, docName);
                          }}
                          disabled={isDisabled}
                        >
                          <FaTrashAlt className="trash-icon" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {isModalOpen && (
              <Modal modalHeading={modalHeading} modalMessage={modalMessage} onClick={() => setIsModalOpen(false)} />
            )}
          </div>
          {showOptionalArrow && (
            <div className="scroll-indicator" onClick={() => handleScrollDown(optionalScrollRef)}>
              <FaChevronDown size={16} />
            </div>
          )}
        </>
      )}

      {/* Modals imported from Modals.js */}
    
    </div>
      <ValidationModal
        isOpen={modalOpen}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
      />

     <UploadModal 
  uploadModalOpen={uploadModalOpen}
  onClose={() => setUploadModalOpen(false)}
  currentDocType={currentDocType}
  file={file}
  fileName={fileName} // ✅ Add this
  setFileName={setFileName} // optional, if needed
  dragging={dragging}
  handleDragOver={handleDragOver}
  handleDragLeave={handleDragLeave}
  handleDrop={handleDrop}
  handleFileChange={handleFileChange}
  handleFileUpload={handleFileUpload}
  fileDescriptions={fileDescriptions}
  handleDescriptionChange={handleDescriptionChange}
  certifiedCheckbox={certifiedCheckbox}
  setCertifiedCheckbox={setCertifiedCheckbox}
/>


      <PreviewModal
        isOpen={previewModalOpen}
        onClose={() => { setPreviewModalOpen(false); setPreviewFile(null); }}
        docType={currentDocType}
        documents={documents}
        optionalAttachments={optionalAttachments}
        modalRef={modalRef}
        deleteDocument={requestDelete}
        setModalMessage={setModalMessage}
        setModalOpen={setModalOpen}
        previewFile={previewFile}
      />

      {/* Delete Confirmation Modal */}
      {confirmDeleteOpen && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1100,
        }}>
          <div style={{
            background: "white",
            padding: "24px",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px",
            width: "90%",
          }}>
            <h3 style={{ margin: "0 0 12px 0" }}>Confirm Delete</h3>
            <p style={{ margin: "0 0 20px 0", color: "#555" }}>
              Are you sure you want to delete <strong>{pendingDeleteRef.current?.docType}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
              <button
                onClick={cancelDelete}
                style={{
                  padding: "8px 20px",
                  border: "1px solid #ccc",
                  background: "white",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  background: "#e74c3c",
                  color: "white",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
</>
  );
});

export default RightSidebar;